import React, { useState, useEffect } from "react";
import { Box, Modal, IconButton, Typography, Fade } from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Close,
  Image as ImageIcon,
} from "@mui/icons-material";
import { useSwipeable } from "react-swipeable";

const isVideo = (url) => /\.(mp4|mov|webm|ogg)$/i.test(url);

const MediaGallery = ({ media }) => {
  const items = [
    ...(media?.tourImages || []),
    ...(media?.tourVideos || []),
    ...(media?.additionalFiles || []),
  ];

  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleOpen = (index) => {
    setCurrentIndex(index);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);
  const prevItem = () =>
    setCurrentIndex((i) => (i === 0 ? items.length - 1 : i - 1));
  const nextItem = () =>
    setCurrentIndex((i) => (i === items.length - 1 ? 0 : i + 1));

  useEffect(() => {
    if (items.length <= 1) return;
    const interval = setInterval(() => {
      nextItem();
    }, 3000);
    return () => clearInterval(interval);
  }, [items.length]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: nextItem,
    onSwipedRight: prevItem,
    trackMouse: true,
  });

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "ArrowLeft") prevItem();
      if (e.key === "ArrowRight") nextItem();
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open]);

  if (!items.length) {
    return (
      <Box
        sx={{
          height: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          bgcolor: "grey.100",
          borderRadius: 2,
          border: "1px dashed",
          borderColor: "grey.400",
        }}
      >
        <ImageIcon fontSize="large" sx={{ mb: 1, color: "grey.600" }} />
        <Typography variant="body2" color="text.secondary">
          No media available
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          width: "100%",
          display: { xs: "block", sm: "grid" },
          gridTemplateColumns: { sm: "2fr 1fr" },
          gap: 1,
          position: "relative",
        }}
      >
        <Box sx={{ display: { xs: "none", sm: "block" } }}>
          <Box
            component="img"
            src={items[0]}
            alt="main-media"
            loading="lazy"
            onClick={() => handleOpen(0)}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: 2,
              display: "block",
              cursor: "pointer",
            }}
            onError={(e) => (e.target.src = "/fallback.jpg")}
          />
        </Box>

        <Box
          sx={{
            display: { xs: "none", sm: "grid" },
            gap: 1,
          }}
        >
          {items.slice(1, 4).map((item, idx) => (
            <Box
              key={idx + 1}
              sx={{
                position: "relative",
                overflow: "hidden",
                borderRadius: 2,
                cursor: "pointer",
                "& img": {
                  transition: "transform 0.3s ease",
                },
                "&:hover img": {
                  transform: "scale(1.05)",
                },
              }}
              onClick={() => handleOpen(idx + 1)}
            >
              <Box
                component="img"
                src={item}
                alt={`thumb-${idx + 1}`}
                loading="lazy"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                onError={(e) => (e.target.src = "/fallback.jpg")}
              />

              {idx === 2 && items.length > 4 && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    bgcolor: "rgba(0,0,0,0.5)",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 20,
                    fontWeight: "bold",
                  }}
                >
                  +{items.length - 4}
                </Box>
              )}
            </Box>
          ))}
        </Box>

        <Box
          {...swipeHandlers}
          sx={{
            display: { xs: "flex", sm: "none" },
            overflowX: "hidden",
            width: "100%",
            position: "relative",
          }}
        >
          {items.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                minWidth: "100%",
                height: 200,
                borderRadius: 2,
                overflow: "hidden",
                transform: `translateX(${(idx - currentIndex) * 100}%)`,
                transition: "transform 0.6s ease-in-out",
                cursor: "pointer",
              }}
              onClick={() => handleOpen(idx)}
            >
              {isVideo(item) ? (
                <video
                  src={item}
                  style={{ width: "100%", height: 200, objectFit: "cover" }}
                />
              ) : (
                <img
                  src={item}
                  alt={`media-${idx}`}
                  style={{ width: "100%", height: 200, objectFit: "cover" }}
                  onError={(e) => (e.target.src = "/fallback.jpg")}
                />
              )}
            </Box>
          ))}

          <Box
            sx={{
              position: "absolute",
              bottom: 8,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: 1,
            }}
          >
            {items.map((_, idx) => (
              <Box
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: idx === currentIndex ? "white" : "grey.500",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
      <Modal open={open} onClose={handleClose} closeAfterTransition>
        <Fade in={open}>
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              bgcolor: "rgba(0,0,0,0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconButton
              onClick={handleClose}
              aria-label="Close"
              sx={{ position: "absolute", top: 16, right: 16, color: "white" }}
            >
              <Close />
            </IconButton>

            <IconButton
              onClick={prevItem}
              aria-label="Previous"
              sx={{ position: "absolute", left: 16, color: "white" }}
            >
              <ChevronLeft fontSize="large" />
            </IconButton>

            <Box maxWidth="80%" maxHeight="80%">
              {isVideo(items[currentIndex]) ? (
                <video
                  src={items[currentIndex]}
                  controls
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    borderRadius: 8,
                  }}
                />
              ) : (
                <img
                  src={items[currentIndex]}
                  alt={`media-${currentIndex}`}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    borderRadius: 8,
                  }}
                  onError={(e) => (e.target.src = "/fallback.jpg")}
                />
              )}
            </Box>

            <IconButton
              onClick={nextItem}
              aria-label="Next"
              sx={{ position: "absolute", right: 16, color: "white" }}
            >
              <ChevronRight fontSize="large" />
            </IconButton>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default MediaGallery;
